"use client"

import type { ChurchFinanceCash, ChurchFinanceCashPoint } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import { isNegativeMoney, monthShortLabel } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { useLocale, useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"

const isZero = (value: string) => Number(value) === 0

/**
 * Church treasury (F3): what the whole church holds today, how the period moved it, where it sits (the branches
 * or the parish) and the end-of-month trend. Every figure is the server's.
 */
export function ChurchCashTile({ cash, currency }: { cash: ChurchFinanceCash; currency: string }) {
  const t = useTranslations("finance.overview")
  const negative = isNegativeMoney(cash.periodChange)
  const empty = isZero(cash.total) && cash.trend.every((point) => isZero(point.balance))

  return (
    <HomeTile title={t("widgets.churchCash.title")} subtitle={t("widgets.churchCash.subtitle")} testId='church-tile-cash'>
      {empty ? (
        <HomeTileEmpty message={t("church.cash.empty")} />
      ) : (
        <>
          <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
            <span
              data-testid='church-cash-total'
              className='text-[2rem] leading-none font-extrabold tracking-tight whitespace-nowrap text-foreground tabular-nums'
            >
              {formatCurrency(cash.total, currency)}
            </span>
            <span
              data-testid='church-cash-change'
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap tabular-nums",
                negative
                  ? "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"
                  : "bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
              )}
            >
              {t("church.cash.periodChange", {
                amount: `${negative ? "" : "+"}${formatCurrency(cash.periodChange, currency)}`,
              })}
            </span>
          </div>
          <dl className='mt-1 flex flex-col gap-1.5 text-[13px]'>
            <div className='flex justify-between gap-3'>
              <dt className='text-muted-foreground'>{t("church.cash.inBranches")}</dt>
              <dd className='font-bold tabular-nums'>{formatCurrency(cash.inBranches, currency)}</dd>
            </div>
            <div className='flex justify-between gap-3'>
              <dt className='text-muted-foreground'>{t("church.cash.atParish")}</dt>
              <dd className='font-bold tabular-nums'>{formatCurrency(cash.atParish, currency)}</dd>
            </div>
          </dl>
          <CashTrend points={cash.trend} currency={currency} />
        </>
      )}
    </HomeTile>
  )
}

const WIDTH = 300
const HEIGHT = 64

/** A small area chart of end-of-month cash. Only its geometry turns money into numbers; labels stay formatted. */
function CashTrend({ points, currency }: { points: ChurchFinanceCashPoint[]; currency: string }) {
  const t = useTranslations("finance.overview.church.cash")
  const locale = useLocale()
  if (points.length < 2) return null

  const values = points.map((point) => Number(point.balance)).map((value) => (Number.isFinite(value) ? value : 0))
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const coords = values.map((value, index) => ({
    x: (index / (values.length - 1)) * WIDTH,
    // A little headroom so the line never sits on the tile edge.
    y: HEIGHT - 6 - ((value - min) / span) * (HEIGHT - 12),
  }))
  const line = coords.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ")
  const area = `${line} L${WIDTH},${HEIGHT} L0,${HEIGHT} Z`
  const first = points[0]!
  const last = points[points.length - 1]!

  return (
    <div className='mt-auto flex min-h-0 flex-col gap-1'>
      <span className='text-xs font-bold text-muted-foreground'>{t("trendLabel", { count: points.length })}</span>
      <svg
        data-testid='church-cash-trend'
        role='img'
        aria-label={points
          .map((point) => t("trendPoint", { month: monthShortLabel(point.month, locale), amount: formatCurrency(point.balance, currency) }))
          .join(", ")}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio='none'
        className='h-16 w-full text-teal-600 dark:text-teal-400'
      >
        <path d={area} fill='currentColor' fillOpacity={0.12} />
        <path d={line} fill='none' stroke='currentColor' strokeWidth={2} vectorEffect='non-scaling-stroke' />
      </svg>
      <span className='flex justify-between text-[11px] text-muted-foreground'>
        <span>{monthShortLabel(first.month, locale)}</span>
        <span>{monthShortLabel(last.month, locale)}</span>
      </span>
    </div>
  )
}
